import axios from "axios";
import moment from 'moment';
import { config } from "../../config";
import { LocalStorage } from "../../data/localstorage";

import { QueryInfo } from "../../util";
import { ChargingEstimate } from '../../entity/station/charging-estimate';
import { Slot } from "../../entity/slot";
import { Vehicle } from '@/entity/vehicle/vehicle';
import { EVCharger } from '@/entity/station/evcharger';
import { User } from '@/entity/authentication/user';
import { EVChargingReservation, EVChargingReservationStatus, EVChargingReservationCancelReason, EVChargingReservationTypeCode, ChargingReservationTypeInfo, EVChargingReservationProgress } from '@/entity/station/reservation';
import { EVChargingReservationTransformer } from '../transformers/evcreservation';
import { EVCBayOutletGun } from '@/entity/station/evcbay';
import { Reservation } from '@/store/modules/Reservations';
import { Result } from '..';

export interface ReservationQuery {
    id?: string
    station?: {
        name?: string
    }
    customer?: {
        name?: string
    }
    reservedBetween?: {
        from?: Date,
        to?: Date
    }
    types?: EVChargingReservationTypeCode[]
    status?: EVChargingReservationStatus
    invoicedBetween?: {
        from?: Date,
        to?: Date
    }
    invoiceNumber?: string
    stopReason?: string
}

export interface chargerStatusQuery {
    chargerId?: string
    connectorId?: string
    chargerType?: string
    installationId? : string
}

export class EVCStationReservationService {
    async estimateForChargingOnStationGunBetweenForUserOnExistingReservationIfAny(gun: EVCBayOutletGun, from: Date, to: Date, user: User, vehicle: Vehicle, reservation?: Reservation | { id: number }): Promise<{
        immediate: ChargingEstimate,
        nextPossible: ChargingEstimate
    }> {
            let estimationQueryInfo = new QueryInfo({
            reservationId: reservation ? reservation.id : 0,
            reservationType: {
                code: EVChargingReservationTypeCode.ChargeForDuration
            },
            startTime: moment(from).format(),
            endTime: moment(to).format(),
            installationDetail: {
                installationId: gun.setup ? gun.setup.id : ""
            },
            chargerType: gun.setup ? (gun.setup.charger as EVCharger).type.id : "",
            outputId: (gun.outlet && gun.outlet.output) ? gun.outlet.output.id : "",
            outputNumber: (gun.outlet && gun.outlet.output) ? gun.outlet.output.number : 0,
            portId: gun.port ? gun.port.id : 0,
            vehicle: {
                vehicleId: vehicle.id
            },
            onBoardPower : vehicle.onboardPower
        })
        let response = await axios.get(config.api.host + "/accounts/" + user.id + "/reservations/nextslot" + estimationQueryInfo.toURLString(), {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let responseInfo = response.data.data
        let estimation = {

        }
        if (responseInfo.estimate != null) {
            estimation = {
                ...estimation,
                immediate: new ChargingEstimate(
                    new Slot(
                        new Date(),
                        moment(responseInfo.estimate.endTime, "YYYY-MM-DD HH:mm").toDate()
                    ),
                    responseInfo.estimate.estimatedNettCost
                )
            }
        }
        if (responseInfo.nextSlot != null) {
            estimation = {
                ...estimation,
                nextPossible: new ChargingEstimate(
                    new Slot(
                        moment(responseInfo.nextSlot, "YYYY-MM-DD HH:mm:SS").toDate(),
                        moment(responseInfo.nextSlotEstimate.endTime, "YYYY-MM-DD HH:mm:SS").toDate()
                    ),
                    responseInfo.nextSlotEstimate.estimatedNettCost
                )
            }
        }
        return estimation as {
            immediate: ChargingEstimate,
            nextPossible: ChargingEstimate
        }
    }

    async estimateForChargingOnStationGunUptoPercentageForUserOnExistingReservationIfAny(gun: EVCBayOutletGun, on: Date, percentage: number, user: User, vehicle: Vehicle, reservation?: Reservation | { id: number }): Promise<{
        immediate: ChargingEstimate,
        nextPossible: ChargingEstimate
    }> {
        let estimationQueryInfo = new QueryInfo({
            reservationId: reservation ? reservation.id : 0,
            reservationType: {
                code: EVChargingReservationTypeCode.ChargeUptoCapacity
            },
            currentBatteryCapacity: percentage,
            startTime: moment(on).format(),
            installationDetail: {
                installationId: gun.setup ? gun.setup.id : ""
            },
            chargerType: gun.setup ? (gun.setup.charger as EVCharger).type.id : "",
            outputId: (gun.outlet && gun.outlet.output) ? gun.outlet.output.id : "",
            outputNumber: (gun.outlet && gun.outlet.output) ? gun.outlet.output.number : 0,
            portId: gun.port ? gun.port.id : 0,
            vehicle: {
                vehicleId: vehicle.id
            }
        })
        let response = await axios.get(config.api.host + "/accounts/" + user.id + "/reservations/nextslot" + estimationQueryInfo.toURLString(), {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let responseInfo = response.data.data
        let estimation = {

        }
        if (responseInfo.estimate != null) {
            estimation = {
                ...estimation,
                immediate: new ChargingEstimate(
                    new Slot(
                        new Date(),
                        moment(responseInfo.estimate.endTime, "YYYY-MM-DD HH:mm").toDate()
                    ),
                    responseInfo.estimate.estimatedNettCost
                )
            }
        }
        if (responseInfo.nextSlot != null) {
            estimation = {
                ...estimation,
                nextPossible: new ChargingEstimate(
                    new Slot(
                        moment(responseInfo.nextSlot, "YYYY-MM-DD HH:mm:SS").toDate(),
                        moment(responseInfo.nextSlotEstimate.endTime, "YYYY-MM-DD HH:mm:SS").toDate()
                    ),
                    responseInfo.nextSlotEstimate.estimatedNettCost
                )
            }
        }
        return estimation as {
            immediate: ChargingEstimate,
            nextPossible: ChargingEstimate
        }
    }

    async estimateForChargingOnStationGunByUnitBasedForUserOnExistingReservationIfAny(gun: EVCBayOutletGun, on: Date, unit: number, user: User, vehicle: Vehicle, reservation?: Reservation | { id: number }): Promise<{
        immediate: ChargingEstimate,
        nextPossible: ChargingEstimate
    }> {
        let estimationQueryInfo = new QueryInfo({
            reservationId: reservation ? reservation.id : 0,
            reservationType: {
                code: EVChargingReservationTypeCode.ChargeForUnit
            },
            unitToCharge: unit,
            startTime: moment(on).format(),
            installationDetail: {
                installationId: gun.setup ? gun.setup.id : ""
            },
            chargerType: gun.setup ? (gun.setup.charger as EVCharger).type.id : "",
            outputId: (gun.outlet && gun.outlet.output) ? gun.outlet.output.id : "",
            outputNumber: (gun.outlet && gun.outlet.output) ? gun.outlet.output.number : 0,
            portId: gun.port ? gun.port.id : 0,
            vehicle: {
                vehicleId: vehicle.id
            }
        })
        let response = await axios.get(config.api.host + "/accounts/" + user.id + "/reservations/nextslot" + estimationQueryInfo.toURLString(), {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let responseInfo = response.data.data
        let estimation = {

        }
        if (responseInfo.estimate != null) {
            estimation = {
                ...estimation,
                immediate: new ChargingEstimate(
                    new Slot(
                        new Date(),
                        moment(responseInfo.estimate.endTime, "YYYY-MM-DD HH:mm").toDate()
                    ),
                    responseInfo.estimate.estimatedNettCost
                )
            }
        }
        if (responseInfo.nextSlot != null) {
            estimation = {
                ...estimation,
                nextPossible: new ChargingEstimate(
                    new Slot(
                        moment(responseInfo.nextSlot, "YYYY-MM-DD HH:mm:SS").toDate(),
                        moment(responseInfo.nextSlotEstimate.endTime, "YYYY-MM-DD HH:mm:SS").toDate()
                    ),
                    responseInfo.nextSlotEstimate.estimatedNettCost
                )
            }
        }
        return estimation as {
            immediate: ChargingEstimate,
            nextPossible: ChargingEstimate
        }
    }

    async estimateForChargingOnStationGunByAmountBasedForUserOnExistingReservationIfAny(gun: EVCBayOutletGun, on: Date, amount: number, user: User, vehicle: Vehicle, reservation?: Reservation | { id: number }): Promise<{
        immediate: ChargingEstimate,
        nextPossible: ChargingEstimate
    }> {
        let estimationQueryInfo = new QueryInfo({
            reservationId: reservation ? reservation.id : 0,
            reservationType: {
                code: EVChargingReservationTypeCode.ChargeForAmount
            },
            amountToCharge: amount,
            startTime: moment(on).format(),
            installationDetail: {
                installationId: gun.setup ? gun.setup.id : ""
            },
            chargerType: gun.setup ? (gun.setup.charger as EVCharger).type.id : "",
            outputId: (gun.outlet && gun.outlet.output) ? gun.outlet.output.id : "",
            outputNumber: (gun.outlet && gun.outlet.output) ? gun.outlet.output.number : 0,
            portId: gun.port ? gun.port.id : 0,
            vehicle: {
                vehicleId: vehicle.id
            }
        })
        let response = await axios.get(config.api.host + "/accounts/" + user.id + "/reservations/nextslot" + estimationQueryInfo.toURLString(), {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let responseInfo = response.data.data
        let estimation = {

        }
        if (responseInfo.estimate != null) {
            estimation = {
                ...estimation,
                immediate: new ChargingEstimate(
                    new Slot(
                        new Date(),
                        moment(responseInfo.estimate.endTime, "YYYY-MM-DD HH:mm").toDate()
                    ),
                    responseInfo.estimate.estimatedNettCost
                )
            }
        }
        if (responseInfo.nextSlot != null) {
            estimation = {
                ...estimation,
                nextPossible: new ChargingEstimate(
                    new Slot(
                        moment(responseInfo.nextSlot, "YYYY-MM-DD HH:mm:SS").toDate(),
                        moment(responseInfo.nextSlotEstimate.endTime, "YYYY-MM-DD HH:mm:SS").toDate()
                    ),
                    responseInfo.nextSlotEstimate.estimatedNettCost
                )
            }
        }
        return estimation as {
            immediate: ChargingEstimate,
            nextPossible: ChargingEstimate
        }
    }

    async reserveToChargeVehicleWithStationGunBetweenAtCostForUser(vehicle: Vehicle, gun: EVCBayOutletGun, from: Date, to: Date, cost: number, user: User): Promise<EVChargingReservation> {
        let response = await axios.post(config.api.host + "/accounts/" + user.id + "/reservations", {
            data: this.reservationInfoToChargeVehicleWithStationGunBetweenAtCostForUser(vehicle, gun, from, to, cost, user)
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let reservationInfo = response.data.data
        let reservation = new EVChargingReservationTransformer().transform(reservationInfo)
        return Promise.resolve(reservation)
    }

    async changeReservationForChargingVehicleWithStationGunBetweenAtCostForUser(reservation: { id: string } | Reservation, vehicle: Vehicle, gun: EVCBayOutletGun, from: Date, to: Date, cost: number, user: User): Promise<EVChargingReservation> {
        let response = await axios.put(config.api.host + "/accounts/" + user.id + "/reservations/" + reservation.id, {
            data: {
                reservationId: reservation.id,
                ...this.reservationInfoToChargeVehicleWithStationGunBetweenAtCostForUser(vehicle, gun, from, to, cost, user)
            }
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let reservationInfo = response.data.data
        let createdReservation = new EVChargingReservationTransformer().transform(reservationInfo)
        return Promise.resolve(createdReservation)
    }

    reservationInfoToChargeVehicleWithStationGunBetweenAtCostForUser(vehicle: Vehicle, gun: EVCBayOutletGun, from: Date, to: Date, cost: number, user: User): any {
        return {
            user: {
                userId: user.id
            },
            vehicle: {
                vehicleId: vehicle.id
            },
            reservationType: {
                code: EVChargingReservationTypeCode.ChargeForDuration
            },
            startTime: moment(from).format(),
            endTime: moment(to).format(),
            installationDetail: {
                installationId: gun.setup ? gun.setup.id : ""
            },
            chargerType: gun.setup ? (gun.setup.charger as EVCharger).type.id : "",
            outputId: (gun.outlet && gun.outlet.output) ? gun.outlet.output.id : "",
            outputNumber: (gun.outlet && gun.outlet.output) ? gun.outlet.output.number : 0,
            portId: gun.port ? gun.port.id : 0,
            estimatedNettCost: cost
        }
    }

    async reserveToChargeVehicleUptoPercentageBetweenAtCostForUser(vehicle: Vehicle, gun: EVCBayOutletGun, percentage: number, from: Date, to: Date, cost: number, user: User): Promise<EVChargingReservation> {
        let response = await axios.post(config.api.host + "/accounts/" + user.id + "/reservations", {
            data: this.reservationInfoToChargeVehicleUptoPercentageBetweenAtCostForUser(vehicle, gun, percentage, from, to, cost, user)
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let reservationInfo = response.data.data
        let reservation = new EVChargingReservationTransformer().transform(reservationInfo)
        return Promise.resolve(reservation)
    }

    async changeReservationForChargingVehicleUptoPercentageBetweenAtCostForUser(reservation: { id: string } | Reservation, vehicle: Vehicle, gun: EVCBayOutletGun, percentage: number, from: Date, to: Date, cost: number, user: User): Promise<EVChargingReservation> {
        let response = await axios.post(config.api.host + "/accounts/" + user.id + "/reservations", {
            data: {
                reservationId: reservation.id,
                ...this.reservationInfoToChargeVehicleUptoPercentageBetweenAtCostForUser(vehicle, gun, percentage, from, to, cost, user)
            }
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let reservationInfo = response.data.data
        let createdReservation = new EVChargingReservationTransformer().transform(reservationInfo)
        return Promise.resolve(createdReservation)
    }

    reservationInfoToChargeVehicleUptoPercentageBetweenAtCostForUser(vehicle: Vehicle, gun: EVCBayOutletGun, percentage: number, from: Date, to: Date, cost: number, user: User): any {
        return {
            user: {
                userId: user.id
            },
            vehicle: {
                vehicleId: vehicle.id
            },
            reservationType: {
                code: EVChargingReservationTypeCode.ChargeUptoCapacity
            },
            currentBatteryCapacity: percentage,
            startTime: moment(from).format(),
            endTime: moment(to).format(),
            installationDetail: {
                installationId: gun.setup ? gun.setup.id : ""
            },
            chargerType: gun.setup ? (gun.setup.charger as EVCharger).type.id : "",
            outputId: (gun.outlet && gun.outlet.output) ? gun.outlet.output.id : "",
            outputNumber: (gun.outlet && gun.outlet.output) ? gun.outlet.output.number : 0,
            portId: gun.port ? gun.port.id : 0,
            estimatedNettCost: cost
        }
    }

    async reserveToChargeVehicleByUnitBasedAtCostForUser(vehicle: Vehicle, gun: EVCBayOutletGun, unit: number, from: Date, to: Date, cost: number, user: User): Promise<EVChargingReservation> {
        let response = await axios.post(config.api.host + "/accounts/" + user.id + "/reservations", {
            data: this.reservationInfoToChargeVehicleByUnitBasedAtCostForUser(vehicle, gun, unit, from, to, cost, user)
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let reservationInfo = response.data.data
        let reservation = new EVChargingReservationTransformer().transform(reservationInfo)
        return Promise.resolve(reservation)
    }

    reservationInfoToChargeVehicleByUnitBasedAtCostForUser(vehicle: Vehicle, gun: EVCBayOutletGun, unit: number, from: Date, to: Date, cost: number, user: User): any {
        return {
            user: {
                userId: user.id
            },
            vehicle: {
                vehicleId: vehicle.id
            },
            reservationType: {
                code: EVChargingReservationTypeCode.ChargeForUnit
            },
            unitToCharge: unit,
            startTime: moment(from).format(),
            endTime: moment(to).format(),
            installationDetail: {
                installationId: gun.setup ? gun.setup.id : ""
            },
            chargerType: gun.setup ? (gun.setup.charger as EVCharger).type.id : "",
            outputId: (gun.outlet && gun.outlet.output) ? gun.outlet.output.id : "",
            outputNumber: (gun.outlet && gun.outlet.output) ? gun.outlet.output.number : 0,
            portId: gun.port ? gun.port.id : 0,
            estimatedNettCost: cost
        }
    }

    async reserveToChargeVehicleByAmountBasedAtCostForUser(vehicle: Vehicle, gun: EVCBayOutletGun, amount: number, from: Date, to: Date, cost: number, user: User): Promise<EVChargingReservation> {
        let response = await axios.post(config.api.host + "/accounts/" + user.id + "/reservations", {
            data: this.reservationInfoToChargeVehicleByAmountBasedAtCostForUser(vehicle, gun, amount, from, to, cost, user)
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let reservationInfo = response.data.data
        let reservation = new EVChargingReservationTransformer().transform(reservationInfo)
        return Promise.resolve(reservation)
    }

    reservationInfoToChargeVehicleByAmountBasedAtCostForUser(vehicle: Vehicle, gun: EVCBayOutletGun, amount: number, from: Date, to: Date, cost: number, user: User): any {
        return {
            user: {
                userId: user.id
            },
            vehicle: {
                vehicleId: vehicle.id
            },
            reservationType: {
                code: EVChargingReservationTypeCode.ChargeForAmount
            },
            amountToCharge: amount,
            startTime: moment(from).format(),
            endTime: moment(to).format(),
            installationDetail: {
                installationId: gun.setup ? gun.setup.id : ""
            },
            chargerType: gun.setup ? (gun.setup.charger as EVCharger).type.id : "",
            outputId: (gun.outlet && gun.outlet.output) ? gun.outlet.output.id : "",
            outputNumber: (gun.outlet && gun.outlet.output) ? gun.outlet.output.number : 0,
            portId: gun.port ? gun.port.id : 0,
            estimatedNettCost: cost
        }
    }

    async reservationsAccessibleByUserAtPageByPaginatedWithSizeMatchingQuery(user: User, page: number, size: number, query: ReservationQuery): Promise<Result<EVChargingReservation>> {
        let queryInfo: any = {
            page: page,
            "page-size": size
        }
        if (query.station && query.station.name) {
            queryInfo = {
                ...queryInfo,
                stationName: query.station.name
            }
        }
        if (query.id) {
            queryInfo = {
                ...queryInfo,
                reservationNo: query.id
            }
        }
        if (query.customer && query.customer.name) {
            queryInfo = {
                ...queryInfo,
                customerName: query.customer.name
            }
        }
        if (query.reservedBetween) {
            if (query.reservedBetween.from) {
                queryInfo = {
                    ...queryInfo,
                    // reservedFrom: moment(query.reservedBetween.from).utc().format("YYYY-MM-DD")
                    reservedFrom: moment(query.reservedBetween.from).format("YYYY-MM-DD")
                }
            }
            if (query.reservedBetween.to) {
                queryInfo = {
                    ...queryInfo,
                    // reservedTo: moment(query.reservedBetween.to).utc().format("YYYY-MM-DD")
                    reservedTo: moment(query.reservedBetween.to).format("YYYY-MM-DD")
                }
            }
        }
        if (query.types && query.types.length) {
            query.types.forEach(element => {

            });
        }
        if (query.status) {
            queryInfo = {
                ...queryInfo,
                status: {
                    code: query.status.id
                }
            }
        }
        if (query.invoicedBetween) {
            if (query.invoicedBetween.from) {
                queryInfo = {
                    ...queryInfo,
                    // reservedFrom: moment(query.invoicedBetween.from).utc().format("YYYY-MM-DD")
                    invoiceFrom: moment(query.invoicedBetween.from).format("YYYY-MM-DD")
                }
            }
            if (query.invoicedBetween.to) {
                queryInfo = {
                    ...queryInfo,
                    // reservedTo: moment(query.invoicedBetween.to).utc().format("YYYY-MM-DD")
                    invoiceTo: moment(query.invoicedBetween.to).format("YYYY-MM-DD")
                }
            }
        }
        if (query.invoiceNumber) {
            queryInfo = {
                ...queryInfo,
                invoiceNo: query.invoiceNumber
            }
        }
        if (query.invoiceNumber) {
            queryInfo = {
                ...queryInfo,
                invoiceNo: query.invoiceNumber
            }
        }
        if (query.stopReason) {
            queryInfo = {
                ...queryInfo,
                stopReason: query.stopReason
            }
        }
        let response = await axios.get(config.api.host + "/reservations/search" + new QueryInfo(queryInfo).toURLString(), {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )
        let reservationsInfos: any[] = response.data.data
        let reservations: EVChargingReservation[] = []
        reservationsInfos.forEach(reservationInfo => {
            let stationInfo: any = reservationInfo.station
            reservations.push(new EVChargingReservationTransformer().transform(reservationInfo))
        });
        return Promise.resolve(new Result<EVChargingReservation>(response.data.meta.totalrecords, reservations))
    }

    async reservationsReservedByUser(user: User, status: EVChargingReservationStatus, page: number, size: number): Promise<EVChargingReservation[]> {
        let queryInfo: any = {
            userId: user.id,
            page: page,
            "page-size": size
        }
        if (status) {
            queryInfo = {
                ...queryInfo,
                status: { code: status.id }
            }
        }
        let response = await axios.get(config.api.host + "/accounts/" + user.id + "/reservations" + new QueryInfo(queryInfo).toURLString(), {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )
        let reservationsInfos: any[] = response.data.data
        let reservations: EVChargingReservation[] = []
        reservationsInfos.forEach(reservationInfo => {
            reservations.push(new EVChargingReservationTransformer().transform(reservationInfo))
        });
        return Promise.resolve(reservations)
    }

    async reservationsMadeByUserForVehicle(user: User, vehicle: Vehicle, page: number, size: number): Promise<EVChargingReservation[]> {
        let response = await axios.get(config.api.host + "/accounts/" + user.id + "/vehicles/" + vehicle.id + "/reservations" + new QueryInfo({
            userId: user.id,
            page: page,
            "page-size": size
        }).toURLString(), {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )
        let reservationsInfos: any[] = response.data.data
        let reservations: EVChargingReservation[] = []
        reservationsInfos.forEach(reservationInfo => {
            reservations.push(new EVChargingReservationTransformer().transform(reservationInfo))
        });
        return Promise.resolve(reservations)
    }

    async reservationWithId(id: string, user: User): Promise<EVChargingReservation> {
        let response = await axios.get(config.api.host + "/reservations/" + id, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )
        let reservationInfo: any[] = response.data.data
        let reservation = new EVChargingReservationTransformer().transform(reservationInfo);
        return Promise.resolve(reservation)
    }

    async reservationWithIdOfUser(id: string, user: User): Promise<EVChargingReservation> {
        let response = await axios.get(config.api.host + "/accounts/" + user.id + "/reservations/" + id + new QueryInfo({
            userId: user.id
        }).toURLString(), {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )
        let reservationInfo: any[] = response.data.data
        let reservation = new EVChargingReservationTransformer().transform(reservationInfo)
        return Promise.resolve(reservation)
    }

    async startChargingReservationByUser(reservation: { id: string } | Reservation, user: User): Promise<void> {
        var response = await axios.post(config.api.host + "/accounts/" + user.id + "/reservations/" + reservation.id + "/start",
            {
            }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )

        return Promise.resolve()
    }

    async verifyOtpForStartCharging(otp: string, reservation: { id: string } | Reservation, user: User): Promise<void> {
        var response = await axios.post(config.api.host + "/accounts/" + user.id + "/reservations/" + reservation.id + "/verifystart",
            {
                data: {
                    otp: otp
                }
            }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )
        return Promise.resolve()
    }

    async verifyGlobalOtp(otp: string, otpData: any, user: User): Promise<void> {
        let otpId = localStorage.getItem('optId')
        var response = await axios.post(config.api.host + "/reservations/globalVerifyOtp",
            {
                data: {
                    otp: otp,
                    details: otpData,
                    userId: user.id,
                    otpId: otpId
                }
            }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve()
    }

    async viewChargingStatusByUser(reservation: { id: string } | Reservation, user: User): Promise<EVChargingReservationProgress> {
        var response = await axios.post(config.api.host + "/accounts/" + user.id + "/reservations/" + reservation.id + "/status",
            {
            }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )
        let chargingInfo = response.data.data;
        let chargingstatus = new EVChargingReservationProgress(chargingInfo.current,
            chargingInfo.percentageCharged,
            chargingInfo.percentageRemaining,
            chargingInfo.power,
            chargingInfo.temperature,
            chargingInfo.timeCompleted,
            chargingInfo.timeRemaining,
            chargingInfo.unitsBalance,
            chargingInfo.unitsConsumed,
            chargingInfo.voltage,
            chargingInfo.refreshTime);
        return Promise.resolve(chargingstatus)
    }

    async stopChargingReservationByUser(reservation: { id: string } | Reservation, user: User): Promise<void> {
        var response = await axios.post(config.api.host + "/accounts/" + user.id + "/reservations/" + reservation.id + "/stop",
            {
            }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )

        return Promise.resolve()
    }

    async cancelReservationForUser(reservation: { id: string } | Reservation, user: User, reason: { id: string, others: string }) {
        var responce = await axios.put(config.api.host + "/accounts/" + user.id + "/reservations/" + reservation.id + "/cancel",
            {
                data: {
                    userId: user.id,
                    cancelReason: {
                        code: reason.id
                    },
                    cancelReasonOthersText: reason.others
                }
            }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )
        return Promise.resolve(responce.data.data)
    }

    async cancelReservationBySuperUser(reservation: { id: string } | Reservation, user: User, reason: { id: string, others: string }): Promise<void> {
        await axios.put(config.api.host + "/reservations/" + reservation.id + "/cancel",
            {
                data: {
                    userId: user.id,
                    cancelReason: {
                        code: reason.id
                    },
                    cancelReasonOthersText: reason.others
                }
            }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )
        return Promise.resolve()
    }

    async rateReservationStation(reservation: { id: string } | Reservation, user: User, rating: number): Promise<void> {
        await axios.post(config.api.host + "/accounts/" + user.id + "/reservations/" + reservation.id + "/rate",
            {
                data: {
                    userId: user.id,
                    rating: rating
                }
            }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )
        return Promise.resolve()
    }

    async reservationStatuses(): Promise<EVChargingReservationStatus[]> {
        let user = new LocalStorage().user
        if (user) {
            let response = await axios.get(config.api.host + "/accounts/" + user.id + "/reservations/status", {
                headers: {
                    Authorization: new LocalStorage().authToken
                }
            })
            let statusesInfos: any[] = response.data.data
            let statuses: EVChargingReservationStatus[] = []
            statusesInfos.forEach(statusInfo => {
                statuses.push(new EVChargingReservationStatus(
                    statusInfo.code,
                    statusInfo.description
                ))
            });
            return Promise.resolve(statuses)
        }
        else {
            throw "Un Authorized"
        }
    }

    async getStopReason(): Promise<any> {
        let userId =  localStorage.getItem("user.id")
        let response = await axios.get(config.api.host + "/v1/accounts/reservations/"+userId+"/getStopReasonList", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve(response.data.data.stopReasons)
    }

    async getErroList(): Promise<any> {
        let userId =  localStorage.getItem("user.id")
        let response = await axios.get(config.api.host + "/v1/admin/"+userId+"/errorCodeList", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve(response.data.data.cmsErrorCode)
    }

    async reservationTypes(): Promise<{ code: EVChargingReservationTypeCode, info: ChargingReservationTypeInfo }[]> {
        let user = new LocalStorage().user
        if (user) {
            let response = await axios.get(config.api.host + "/accounts/" + user.id + "/reservations/types", {
                headers: {
                    Authorization: new LocalStorage().authToken
                }
            })
            let typesInfos: any[] = response.data.data
            let types: { code: EVChargingReservationTypeCode, info: ChargingReservationTypeInfo }[] = []
            typesInfos.forEach(typeInfo => {
                types.push({
                    code: typeInfo.code,
                    info: new ChargingReservationTypeInfo(
                        typeInfo.code,
                        typeInfo.description
                    )
                })
            });
            return Promise.resolve(types)
        }
        else {
            throw "Un Authorized"
        }
    }

    async reservationCancelReasons(): Promise<EVChargingReservationCancelReason[]> {
        let response = await axios.get(config.api.host + "/cancelreasons", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let reasonsInfos: any[] = response.data.data
        let reasons: EVChargingReservationCancelReason[] = []
        reasonsInfos.forEach(reasonsInfo => {
            reasons.push(new EVChargingReservationCancelReason(
                reasonsInfo.code,
                reasonsInfo.description
            ))
        });
        return Promise.resolve(reasons)
    }

    async downloadInvoiceWithIdOfUser(id: string, secret: string, user: User): Promise<any> {
        let response = await axios.get(config.api.host + "/accounts/" + user.id + "/reservations/" + id + "/download/" + secret, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )
        return Promise.resolve(response)
    }

    getDownloadURL(id: string, secret: string, user: User): string {
        let URL = config.api.host + "/accounts/" + user.id + "/reservations/" + id + "/download/" + secret + "/" + id;
        return URL;
    }


    // start charging by QR code 

    async startChargingByQRCode1(reservation: { id: string } | Reservation, user: User): Promise<void> {
        var response = await axios.post(config.api.host + "/accounts/" + user.id + "/reservations/" + reservation.id + "/startByQRCode",
            {
            }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )
    }
     async monitorStartChargerButtonStatus(statusData: any): Promise<void> {
        let userId = localStorage.getItem('user.id')
        var response = await axios.post(config.api.host + "/v1/accounts/reservations/" + userId + "/startChargeButtonStatus" ,
            {
                data: statusData
            }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve(response.data.data)
    }
    async startChargeNowByTime(vehicle: Vehicle, gun: EVCBayOutletGun, from: Date, to: Date, cost: number, user: User): Promise<EVChargingReservation> {
        var response = await axios.post(config.api.host + "/accounts/" + user.id + "/startChargeNow",{
            data: this.reservationInfoToChargeVehicleWithStationGunBetweenAtCostForUser(vehicle, gun, from, to, cost, user)
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve(response.data.data)
    }
    async startChargeNowByCapacity(vehicle: Vehicle, gun: EVCBayOutletGun, percentage: number, from: Date, to: Date, cost: number, user: User): Promise<EVChargingReservation> {
        var response = await axios.post(config.api.host + "/accounts/" + user.id + "/startChargeNow",{
            data: this.reservationInfoToChargeVehicleUptoPercentageBetweenAtCostForUser(vehicle, gun, percentage, from, to, cost, user)
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve(response.data.data)
    }
    async startChargeNowByUnit(vehicle: Vehicle, gun: EVCBayOutletGun, unit: number, from: Date, to: Date, cost: number, user: User): Promise<EVChargingReservation> {
        var response = await axios.post(config.api.host + "/accounts/" + user.id + "/startChargeNow",{
            data: this.reservationInfoToChargeVehicleByUnitBasedAtCostForUser(vehicle, gun, unit, from, to, cost, user)
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve(response.data.data)
    }
    async startChargeNowByAmount(vehicle: Vehicle, gun: EVCBayOutletGun, amount: number, from: Date, to: Date, cost: number, user: User): Promise<EVChargingReservation> {
        var response = await axios.post(config.api.host + "/accounts/" + user.id + "/startChargeNow",{
            data: this.reservationInfoToChargeVehicleByAmountBasedAtCostForUser(vehicle, gun, amount, from, to, cost, user)
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve(response.data.data)
    }
    

    // async startChargingByQRCode(vehicle: Vehicle, gun: EVCBayOutletGun, from: Date, to: Date, cost: number, user: User): Promise<EVChargingReservation> {
    async startChargingByQRCode(vehicle: Vehicle, gun: EVCBayOutletGun, unit: number, from: Date, to: Date, cost: number, user: User): Promise<EVChargingReservation> {
        var response = await axios.post(config.api.host + "/accounts/" + user.id + "/startChargeNow",
        {
            // data: this.reservationInfoToChargeVehicleWithStationGunBetweenAtCostForUser(vehicle, gun, from, to, cost, user)
            data: this.reservationInfoToChargeVehicleByUnitBasedAtCostForUser(vehicle, gun, unit, from, to, cost, user)
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve(response.data.data)
    }

    async getAvailableTime(qrCodeValue: any): Promise<any> {
        var response = await axios.get(config.api.host + "/accounts/reservations/getUpcomingReservation/" + qrCodeValue,
            {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        });
        return Promise.resolve(response.data)
    }

    async getDisplayDuration(): Promise<any> {
        var response = await axios.get(config.api.host + "/accounts/reservations/displayConfirmButtonDuration",
            {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        });
        return Promise.resolve(response.data)
    }

    async getDurationForApiCall(): Promise<any> {
        var response = await axios.get(config.api.host + "/accounts/reservations/durationForApiCall",
            {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        });
        return Promise.resolve(response.data)
    }
    async getProgressBarDuration(): Promise<any> {
        var response = await axios.get(config.api.host + "/accounts/reservations/progressBarDuration",
            {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        });
        return Promise.resolve(response.data)
    }
    async getErrorCodeList(errorCode:any): Promise<any> {
        let userId = localStorage.getItem('user.id')
        var response = await axios.get(config.api.host + "/v1/accounts/reservations/"+ userId +"/errorCodeList/"+ errorCode,
            {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        });
        return Promise.resolve(response.data.data)
    }

    async getReservationInfo(): Promise<any> {
        var response = await axios.get(config.api.host + "/accounts/reservations/displayConfirmButtonDuration",
            {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        });
        return Promise.resolve(response.data)
    }

    async getReservationMessage(): Promise<any> {
        var response = await axios.get(config.api.host + "/accounts/reservations/getReservationMessage",
            {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        });
        return Promise.resolve(response.data.data)
    }
//    

    async getReservationType(slotType: any): Promise<any> {
        let userId = localStorage.getItem('user.id')
        var response = await axios.get(config.api.host + "/v1/accounts/reservations/" + userId + "/displayChargeType/"+slotType,
            {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        });
        return Promise.resolve(response.data.data)
    }

    async getReloadDuration(): Promise<any> {
        let userId = localStorage.getItem('user.id')
        var response = await axios.get(config.api.host + "/v1/accounts/reservations/" + userId + "/reloadDurations",
            {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        });
        return Promise.resolve(response.data.data)
    }
    async getOccupiedMessage(installationId: any): Promise<any> {
        let userId = localStorage.getItem('user.id')
        var response = await axios.get(config.api.host + "/v1/accounts/reservations/" + userId + "/nextAvailableSlot/"+installationId,
            {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        });
        return Promise.resolve(response.data.data)
    }

    async getCurrentChargerStatus(query: chargerStatusQuery): Promise<any> {
        let userId = localStorage.getItem('user.id')
        let queryInfo = {
            chargerId: query.chargerId,
            connectorId: query.connectorId,
            chargerType: query.chargerType,
            installationId: query.installationId
        }
        var response = await axios.get(config.api.host + "/v1/accounts/reservations/" + userId + "/currentChargerStatus" +  new QueryInfo(queryInfo).toURLString(),
            {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        });
        return Promise.resolve(response.data.data)
    }

    async saveReservationUsageDetails(payload: any, userId: any) {
        let loginUserId = localStorage.getItem("user.id")
        let response = await axios.post(config.api.host + "/v1/admin/" + loginUserId  + "/saveReservationUsageDetails", payload, {
            headers: {
                Authorization: new LocalStorage().authToken
            } 
        });
        return response.data;
        
    }
    async getInvoiceDetailsById(reservationId: any) : Promise<any>{
        let loginUserId = localStorage.getItem("user.id")
        let response = await axios.get(config.api.host + "/v1/admin/" +loginUserId +"/getInvoiceDetailsById/"+reservationId, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        }
        )
        return Promise.resolve(response.data.data.data)
    }
}